import React, { FC, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useHistory } from 'react-router-dom';
import loadable from '@loadable/component';
import { Formik, useFormikContext } from 'formik';
import { Trans, useTranslation } from '@blocs.i18n';
import { useUserContext, clearStorage, parseJwt, removeMarketingRedirect, saveMarketingRedirect } from '@blocs.auth';
import {
  Link,
  BodyText,
  FlexCol,
  BodyTextSmall,
  H2Text,
  H1Text,
  GoogleButton,
  DividerWithLabel,
  BodyTextLarge,
} from '@minecraft.atoms';
import { THEME_NAMES, Theme } from '@minecraft.themes';
import { FormWrapper, StyledLoginPageWrapper } from 'styles/Login.style';
import SCREEN_NAMES from 'config/constants/screenNames';
import { getEnvironment } from '@minecraft.environment';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import LoginForm from '../forms/LoginForm';
import { loginInitialValues, loginValidation } from '../forms/LoginMapper';
import {
  getQueryParamsFromSearch,
  removeLocalStorageInviteOrAccessTokens,
  updateLocalStorageRequestToken,
} from '../utils/loginHelpers';
import LoginAlertMessage from './LoginComponents/LoginAlertMessage';
import { useLogInWithGoogle } from './LoginComponents/useLogInWithGoogle';
import { LoginLoader } from './LoginComponents/LoginSkeleton';

const BAUInstanceSelectModalLazy = loadable(
  () => import(/* webpackChunkName: "ula-bau-instance-modal" */ 'components/BAUInstanceSelectModal')
);

const ClickHereLink: FC = ({ children }) => {
  const ULA_URL = getEnvironment()?.ULA_URL;
  return (
    <Link
      to={`${ULA_URL}/forgot-username/back-to-login`}
      target="_self"
      data-qa-id={`${SCREEN_NAMES.LOGIN}-forgot-username-click-here`}
    >
      {children}
    </Link>
  );
};

const RegisterLink: FC<{ to: string }> = ({ to, children }) => {
  const ULA_URL = getEnvironment()?.ULA_URL;

  return (
    <Link to={`${ULA_URL}${to}`} target="_self" data-qa-id={`${SCREEN_NAMES.LOGIN}-link-signup`}>
      {children}
    </Link>
  );
};

type LoginPageVersion = 'default' | 'collaborator' | 'studio';

const getLoginVersion = ({ theme, version }: { theme: Theme; version: string }): LoginPageVersion => {
  if (version === 'collaborator') {
    return 'collaborator';
  }

  if (theme?.name === THEME_NAMES.studio) {
    return 'studio';
  }

  return 'default';
};

const buildRegisterLink = ({ queryParams }: { queryParams: Record<string, string> }): string => {
  const { redirectFromTalent, ...otherParams } = queryParams;
  const baseSignupPath = '/signup';
  const specificSignupPath = redirectFromTalent ? '/TALENT' : '';

  // make sure clearLocalStorage and redirectAudience are not passed to the signup page
  const urlParams = new URLSearchParams(otherParams);
  urlParams.delete('clearLocalStorage');
  urlParams.delete('redirectAudience');
  const queryParamsStr = urlParams.toString();
  const queryStr = queryParamsStr ? `?${queryParamsStr}` : '';

  return `${baseSignupPath}${specificSignupPath}${queryStr}`;
};

interface LoginFormData {
  email: string;
  password: string;
}

const LoginFormBody: FC = () => {
  const routerHistory = useHistory();
  const { t } = useTranslation();
  const isLegacyOnboardingDeprecated = useFeatureExperiment(FEATURE_FLAGS.WEB_ONBOARDING_DEPRECATION);
  const isGoogleSSOEnabled = useFeatureExperiment(FEATURE_FLAGS.WEB_GOOGLE_SSO);
  const { login, loginError, isAuthenticating } = useUserContext();
  const { googleLogin, isLoggingIn } = useLogInWithGoogle();
  const { values } = useFormikContext<LoginFormData>();
  const queryParams = getQueryParamsFromSearch<{
    emailVerified?: string;
    rosterInvite?: string;
    redirectUrl?: string;
    audienceToken?: string;
    redirectFromTalent?: string;
    onBoardingError?: string;
    version?: string;
    utm_source?: string;
    utm_campaign?: string;
    utm_medium?: string;
  }>(routerHistory.location.search);
  const { audienceToken, version = 'default', redirectUrl } = queryParams;
  const registerLink = buildRegisterLink({ queryParams });

  const [isBAUInstanceModalOpen, setIsBAUInstanceModalOpen] = useState(false);
  const theme = useTheme() as Theme;
  const isStudioTheme = theme?.name === THEME_NAMES.studio;
  const loginText = isStudioTheme ? t('login:login.studioHeader') : t('login:login.header');
  const pageVersion = getLoginVersion({ theme, version });

  useEffect(() => {
    // this makes sure the redirectUrl is saved in local storage
    // it's only used if the user goes to the registration page
    // b/c that page can't set localstorage on its own since locally it runs at port 3000 instead of 7777
    // so if it gets set on 3000 the talent app wont pick up the value after the redirect
    if (redirectUrl) {
      saveMarketingRedirect(redirectUrl);
    }

    // this effect will open and close the BAU Instance Modal
    // as needed
    if (loginError) {
      const graphqlErr = loginError?.graphQLErrors?.[0];
      const code = graphqlErr?.data?.errorCode;
      const modalShouldOpen = code === 28;
      const modalShouldClose = [1, 4, 9, 29].includes(code);

      if (!isBAUInstanceModalOpen && modalShouldOpen) {
        setIsBAUInstanceModalOpen(true);
      }

      if (isBAUInstanceModalOpen && modalShouldClose) {
        setIsBAUInstanceModalOpen(false);
      }
    }
  }, [loginError, isBAUInstanceModalOpen]);

  const handleLoginBau = (selectedInstance: string): void => {
    const parsedToken = parseJwt(audienceToken);
    const input = {
      name: values?.email?.trim(),
      password: values?.password?.trim(),
      instance: selectedInstance,
      migratedAccountID: Number(parsedToken?.id) || 0,
    };

    login(input);
  };

  if (isLoggingIn) return <LoginLoader />;

  return (
    <StyledLoginPageWrapper>
      {isBAUInstanceModalOpen && (
        <BAUInstanceSelectModalLazy
          handleSubmit={handleLoginBau}
          disableSubmit={isAuthenticating}
          error={loginError}
          closeModal={() => setIsBAUInstanceModalOpen(false)}
        />
      )}
      <LoginAlertMessage error={loginError} />

      <FlexCol
        grow={0}
        bgColor="headerBanner"
        className="cn_w-full cn_atom_p-4 cn_mb-8"
        justifyContent="center"
        alignItems="center"
        gap="2"
      >
        <H1Text>{loginText}</H1Text>
        <BodyTextLarge>{t('login:login.header.description')}</BodyTextLarge>
      </FlexCol>
      <FormWrapper>
        {pageVersion === 'collaborator' && (
          <FlexCol
            alignItems="center"
            className="cn-w-full cn_atom_p-6"
            wrap="nowrap"
            bgColor="card"
            borderColor="default"
            borderPlacement="all"
            customBorders={{
              top: { borderColor: 'default', borderSize: 0.06 },
              left: { borderColor: 'default', borderSize: 0.06 },
              right: { borderColor: 'default', borderSize: 0.06 },
              bottom: { borderColor: 'default', borderSize: 0 },
            }}
            data-testid="login-collaborator-header"
          >
            <BodyTextSmall fontWeight="semibold">{t('login:login.subhead.collaborator')}</BodyTextSmall>
          </FlexCol>
        )}
        <FlexCol
          alignItems="stretch"
          className="cn-w-full cn_atom_p-6"
          wrap="nowrap"
          bgColor="card"
          borderColor="default"
          borderPlacement="all"
        >
          <H2Text>{t('login:login.formTitle')}</H2Text>
          {isGoogleSSOEnabled && (
            <FlexCol alignItems="stretch" className="cn_mt-8">
              <GoogleButton onClick={() => googleLogin()} label={t('login:login.label.continueWithGoogle')} />
              <DividerWithLabel label={t('login:signup.divider.label')} />
            </FlexCol>
          )}
          <LoginForm loading={isAuthenticating} />
        </FlexCol>
        {pageVersion === 'default' && (
          <>
            <FlexCol
              alignItems="center"
              className="cn-w-full cn_atom_py-4"
              wrap="nowrap"
              bgColor="card"
              borderColor="default"
              borderPlacement="all"
              customBorders={{
                top: { borderColor: 'default', borderSize: 0 },
                left: { borderColor: 'default', borderSize: 0.06 },
                right: { borderColor: 'default', borderSize: 0.06 },
                bottom: { borderColor: 'default', borderSize: 0.06 },
              }}
            >
              <BodyText fontWeight="semibold" data-testid="login-registerlink">
                <Trans i18nKey="login:login.registerText" components={[<RegisterLink to={registerLink} />]} />
              </BodyText>
            </FlexCol>
            {!isLegacyOnboardingDeprecated && (
              <FlexCol className="cn-w-full cn_atom_p-6" wrap="nowrap" data-testid="login-footer-wrapper">
                <BodyTextSmall color="disabled">
                  <b>{t('login:login.subheadSection1')}</b>
                  <br />
                  <br />
                  <b data-testid="login-clickherelink">
                    <Trans i18nKey="login:login.subheadSection2" components={[<ClickHereLink />]} />
                  </b>
                </BodyTextSmall>
              </FlexCol>
            )}
          </>
        )}
      </FormWrapper>
    </StyledLoginPageWrapper>
  );
};

const Login: FC = () => {
  const { t } = useTranslation();
  const { login } = useUserContext();
  const routerHistory = useHistory();
  const { redirectUrl } = getQueryParamsFromSearch(routerHistory.location.search);

  // update localstorage to set roster invitation token
  useEffect(() => {
    updateLocalStorageRequestToken(redirectUrl);
  }, [redirectUrl]);

  return (
    <Formik<LoginFormData>
      initialValues={loginInitialValues}
      validationSchema={loginValidation(t)}
      onSubmit={(values) => {
        // if we have rosterInviteToken or profileAccessToken in storage -> remove them as no need
        removeLocalStorageInviteOrAccessTokens();
        // remove any existing access and refresh tokens
        clearStorage();
        removeMarketingRedirect();
        // login will set the loginError on failure
        // or the tokens and do the redirect on success
        login({
          name: values?.email?.trim(),
          password: values?.password?.trim(),
        });
      }}
    >
      <LoginFormBody />
    </Formik>
  );
};

export default Login;
